import { FC, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import ReactSelect, { SingleValue } from 'react-select';
import TextField from '../textField/TextField';
import {
  countryPhoneCodes,
  countryPhoneCodesWithFlagAndName,
} from '../../../utils/constants/countryPhoneCodes';
import { PhoneNumberFieldProps } from './PhoneNumberField.types';
import { PhoneNumberFieldWrapper } from './PhoneNumberField.styles';

const countryPhoneCodesWithFlagAndNameUSFirst = [
  countryPhoneCodesWithFlagAndName.find(({ id }) => id === 'United States')!,
  ...countryPhoneCodesWithFlagAndName.filter(
    ({ id }) => id !== 'United States',
  ),
];

const PhoneNumberField: FC<PhoneNumberFieldProps> = ({
  name,
  placeholder,
  defaultCountryCode,
  ...rest
}) => {
  const { setValue } = useFormContext();
  const currentValue = useWatch({ name });
  const currentAreaCode = useWatch({ name: `${name}_areaCode` });
  const currentCountryCode =
    useWatch({ name: `${name}_countryCode` }) ||
    countryPhoneCodes.find(({ dial_code }) => dial_code === currentAreaCode)
      ?.code;
  const currentBaseNumber = useWatch({ name: `${name}_base` });

  useEffect(() => {
    if (currentValue) {
      const match = countryPhoneCodesWithFlagAndNameUSFirst.filter(
        ({ value }) => currentValue.startsWith(value),
      );
      const matchFirst = match?.[0];
      const countryCode =
        match.find(({ code }) => code === defaultCountryCode)?.code ||
        matchFirst?.code;

      setValue(`${name}_areaCode`, matchFirst?.value);
      setValue(`${name}_countryCode`, countryCode);
      setValue(`${name}_base`, currentValue.replace(matchFirst?.value, ''));
    } else {
      setValue(`${name}_areaCode`, '+1');
      setValue(`${name}_countryCode`, 'US');
    }
  }, []);

  useEffect(() => {
    if (currentAreaCode && currentBaseNumber) {
      setValue(name, `${currentAreaCode}${currentBaseNumber}`);
    } else {
      setValue(name, null);
    }
  }, [currentAreaCode, currentBaseNumber]);

  const onChangeAreaCode = (
    option: SingleValue<{
      value?: string | undefined;
      label: string | undefined;
      id?: string | undefined;
      code?: string | undefined;
    }> | null,
  ) => {
    setValue(`${name}_areaCode`, option?.value);
    setValue(`${name}_countryCode`, option?.code);
  };

  const defaultValueAreaCode = countryPhoneCodesWithFlagAndNameUSFirst.find(
    ({ id }) => id === 'United States',
  );

  const valueAreaCode = currentCountryCode
    ? countryPhoneCodesWithFlagAndNameUSFirst.find(({ code }) => {
        return code === currentCountryCode;
      }) || defaultValueAreaCode
    : defaultValueAreaCode;

  const formattedValueAreaCode = {
    ...currentAreaCode,
    label: valueAreaCode?.label?.slice(0, valueAreaCode.label.indexOf('(') - 1),
  };

  return (
    <PhoneNumberFieldWrapper>
      <TextField
        groupName={name}
        name={`${name}_base`}
        placeholder={placeholder || '512 345 678'}
        leftAddon={
          <ReactSelect
            isSearchable
            id={`${name}_areaCode`}
            value={formattedValueAreaCode}
            getOptionValue={(option) => option?.code ?? ''}
            onChange={(value) => onChangeAreaCode(value)}
            options={countryPhoneCodesWithFlagAndNameUSFirst}
            classNames={{
              indicatorsContainer: () => 'indicators-container',
              control: () => 'control-container',
              menu: () => 'menu-container',
              singleValue: () => 'single-value-container',
            }}
            components={{
              IndicatorSeparator: () => null,
            }}
          />
        }
        {...rest}
      />
    </PhoneNumberFieldWrapper>
  );
};

export default PhoneNumberField;
